/**
 * Tách câu báo hết hạn mức AI của nền tảng thành loại và số.
 *
 * Nhiều nơi chỉ lưu câu chữ vào cột lỗi của job (`error_message`, `ocr_error`, `loi`, `index_error`...), không còn cấu
 * trúc, nên giao diện đọc ngược từ đúng khuôn câu backend sinh ra. Câu này do nền tảng viết chứ không phải dữ liệu nghiệp
 * vụ, nên sau khi tách được thì giao diện dịch được (ADR-UI-0010 mục 5 chỉ giữ nguyên văn dữ liệu nghiệp vụ).
 *
 * Nguồn câu, có test chéo đọc thẳng các tệp này (`loi-han-muc.test.ts`):
 * - `services/ai-gateway/internal/socai/cau.go`: mọi câu của cổng AI;
 * - `modules/ocr/backend/src/han-muc/han-muc.ts`, `modules/van-ban/.../ai/HanMucAi.java`: câu của đường gọi thẳng;
 * - `modules/bien-ban/backend`: `nhan-dang/cong-nhan-dang.ts`, `tom-tat/tom-tat.ts`;
 * - `modules/tro-ly-ai/backend/src/hoi-dap/joint/han-muc.ts`;
 * - `modules/index/backend/src/chia-doan/cau-truc.ts`: câu bọc của bước không bắt buộc.
 *
 * Luật: khớp **trọn câu**, không khớp thì `null` để nơi gọi hiện câu gốc. Chữ thừa ở đầu hay cuối là thông tin hàm này
 * không hiểu, đoán tiếp là làm mất thông tin. Mã lỗi không quyết định gì, chỉ đi kèm: mã hạn mức mà câu lạ vẫn là `null`.
 */

/**
 * - `het-goi`: hết hạn mức tháng của cả tổ chức (`*_QUOTA_EXCEEDED`, `AI_QUOTA_EXHAUSTED`).
 * - `het-ky`: câu mặc định của Trợ lý AI, không nói rõ phạm vi và kỳ.
 * - `vuot-phong-ban`, `vuot-nguoi`: hết hạn mức Tenant Admin tự đặt (`AI_LIMIT_*`).
 * - `khong-du`: còn nhưng không đủ cho lượt này (`AI_QUOTA_INSUFFICIENT`); xem `phamVi` và `dauKhongDu`.
 * - `chua-kiem`: chưa kiểm được hạn mức (`AI_QUOTA_UNAVAILABLE`), lỗi tạm chứ không phải hết.
 */
export type LoaiHanMuc = 'het-goi' | 'het-ky' | 'vuot-phong-ban' | 'vuot-nguoi' | 'khong-du' | 'chua-kiem';

export type PhamViHanMuc = 'to-chuc' | 'phong-ban' | 'nguoi';
export type KyHanMuc = 'ngay' | 'tuan' | 'thang';
export type DauKhongDu = 'dau-vao' | 'dau-ra' | 'am-thanh';
export type LienHeHanMuc = 'nen-tang' | 'to-chuc' | 'quan-tri';

export interface ThongTinHanMuc {
  loai: LoaiHanMuc;
  /** `null` ở `het-ky` và `chua-kiem`: câu gốc không nêu. */
  phamVi: PhamViHanMuc | null;
  /** Tên phòng ban trong câu; `null` khi câu nói "phòng ban của bạn". Là dữ liệu nghiệp vụ, không dịch. */
  tenPhongBan: string | null;
  tinhNang: string | null;
  ky: KyHanMuc | null;
  daDung: number | null;
  hanMuc: number | null;
  can: number | null;
  con: number | null;
  /** Giờ và ngày mở lại đúng như câu viết: `00:00`, `dd/MM/yyyy`. */
  moLaiGio: string | null;
  moLaiNgay: string | null;
  dauKhongDu: DauKhongDu | null;
  lienHe: LienHeHanMuc | null;
  /** Lượt đang chạy thì bị cắt giữa chừng. */
  biCat: boolean;
  /** Câu gốc khẳng định các chức năng khác vẫn dùng bình thường. */
  khacVanDung: boolean;
  /** Việc chưa làm được, khi câu nêu. */
  viec: 'chep-loi' | 'tom-tat' | null;
  /** Câu gốc khẳng định bản chép lời vẫn dùng được. */
  banChepConDung: boolean;
  /** Tệp đã nạp xong, chỉ bỏ qua bước dựng tiêu đề bằng AI (câu bọc của `index`). */
  boQuaTieuDe: boolean;
  /** `chua-kiem`: hệ thống còn tự thử lại. */
  heThongThuLai: boolean;
  maLoi: string | null;
  cauGoc: string;
}

const SO = String.raw`(\d[\d.]*)`;
const GIO = String.raw`(\d{2}:\d{2})`;
const NGAY = String.raw`(\d{2}/\d{2}/\d{4})`;
/** `hanMucCuaQuyTac` của `cau.go`: ba nhóm là đối tượng, tính năng, kỳ. */
const QUY_TAC = String.raw`hạn mức AI tổ chức đặt cho (bạn|phòng ban của bạn|phòng ban .+?)(?: với tính năng (.+?))? (hôm nay|tuần này|tháng này)`;
/** `moLai` của `cau.go`: hai nhóm là giờ, ngày. */
const MO_LAI = String.raw`Hạn mức mở lại lúc ${GIO} ngày ${NGAY}\. Cần thêm thì liên hệ quản trị viên của tổ chức\.`;

const HET_GOI = new RegExp(String.raw`^Tổ chức đã hết hạn mức token AI tháng này(?: \(đã dùng ${SO} / ${SO} token\))?\.`
  + String.raw`(?: Các chức năng dùng AI tạm dừng tới ${GIO} ngày ${NGAY}; các chức năng khác vẫn dùng bình thường\.)?`
  + String.raw`( Liên hệ quản trị nền tảng nếu cần nâng hạn mức\.)?$`);
const HET_GOI_BI_CAT = new RegExp(String.raw`^Tổ chức vừa dùng hết hạn mức token AI tháng này nên lượt xử lý bị dừng giữa chừng\.`
  + String.raw`(?: Các chức năng dùng AI tạm dừng tới ${GIO} ngày ${NGAY}; liên hệ| Liên hệ) quản trị nền tảng nếu cần nâng hạn mức\.$`);
const VUOT = new RegExp(String.raw`^Đã dùng hết ${QUY_TAC} \(đã dùng ${SO} / ${SO} token\)\. ${MO_LAI}$`);
const VUOT_BI_CAT = new RegExp(String.raw`^Đã dùng hết ${QUY_TAC} nên lượt xử lý bị dừng giữa chừng\. ${MO_LAI}$`);

const KHONG_DU_VAO_QT = new RegExp(String.raw`^Yêu cầu này cần ${SO} token cho phần nội dung gửi đi, ${QUY_TAC} chỉ còn ${SO} token\. Gửi ít nội dung hơn hoặc liên hệ quản trị viên của tổ chức\.$`);
const KHONG_DU_VAO = new RegExp(String.raw`^Yêu cầu này cần ${SO} token cho phần nội dung gửi đi, tổ chức chỉ còn ${SO} token tháng này\. Gửi ít nội dung hơn \(ít trang, ít tài liệu hơn\) hoặc liên hệ quản trị nền tảng để nâng hạn mức\.$`);
const KHONG_DU_AM_QT = new RegExp(String.raw`^Bản ghi này cần ${SO} token, ${QUY_TAC} chỉ còn ${SO} token\. Cắt ngắn bản ghi hoặc liên hệ quản trị viên của tổ chức\.$`);
const KHONG_DU_AM = new RegExp(String.raw`^Bản ghi này cần ${SO} token, tổ chức chỉ còn ${SO} token tháng này\. Cắt ngắn bản ghi hoặc liên hệ quản trị nền tảng để nâng hạn mức\.$`);
const KHONG_DU_RA_QT = new RegExp(String.raw`^Yêu cầu này cần ít nhất ${SO} token cho kết quả, sau phần nội dung gửi đi ${QUY_TAC} chỉ còn ${SO} token\. Liên hệ quản trị viên của tổ chức\.$`);
const KHONG_DU_RA = new RegExp(String.raw`^Yêu cầu này cần ít nhất ${SO} token cho kết quả, sau phần nội dung gửi đi tổ chức chỉ còn ${SO} token tháng này\. Liên hệ quản trị nền tảng để nâng hạn mức\.$`);

const HET_CHEP_LOI = /^Tổ chức đã hết hạn mức AI nên chưa chép lời được bản ghi này\.$/;
const HET_TOM_TAT = /^Tổ chức đã hết hạn mức AI nên chưa tóm tắt được\. Bản chép lời vẫn dùng được; hãy bấm \x22Tóm tắt lại\x22 khi có thêm hạn mức\.$/;
const HET_KY = /^Đã dùng hết hạn mức sử dụng AI của kỳ này\. Liên hệ quản trị viên để được nâng hạn mức\.$/;
const CHUA_KIEM_CONG = /^Chưa kiểm được hạn mức token AI của tổ chức nên tạm chưa gọi được AI\. Thử lại sau ít phút; các chức năng khác vẫn dùng bình thường\.$/;
const CHUA_KIEM_BIEN_BAN = /^Chưa kiểm được hạn mức AI của tổ chức\. Hệ thống sẽ thử lại; nếu vẫn lỗi hãy thử lại sau ít phút\.$/;

/** Đuôi `modules/bien-ban` từng ghép vào câu của cổng; các phiên đã lưu vẫn còn, nút Thử lại nay do giao diện nói. */
const DUOI_THU_LAI = ` Khi có thêm hạn mức, hãy bấm Thử lại.`;
const BOC_TIEU_DE = /^Đã nạp tệp nhưng bỏ qua bước dựng tiêu đề bằng AI: (.+) Đoạn chia có thể kém chính xác hơn; nạp lại tệp sau khi khắc phục để chia đoạn tốt hơn\.$/;

const docSo = (s: string | undefined): number | null => (s === undefined ? null : Number(s.replace(/\./g, '')));

/** Các đoạn dưới viết bằng dấu huyền ngược vì là chữ của khuôn, không phải chuỗi giao diện (`check:i18n` soi hai loại nháy còn lại). */
const kyTuChu = (ky: string): KyHanMuc => (ky === `hôm nay` ? 'ngay' : ky === `tuần này` ? 'tuan' : 'thang');

type PhanQuyTac = Pick<ThongTinHanMuc, 'phamVi' | 'tenPhongBan' | 'tinhNang' | 'ky'>;

function quyTac(ai: string, tinhNang: string | undefined, ky: string): PhanQuyTac {
  const laNguoi = ai === `bạn`;
  const coTen = !laNguoi && ai !== `phòng ban của bạn`;
  return {
    phamVi: laNguoi ? 'nguoi' : 'phong-ban',
    tenPhongBan: coTen ? ai.slice(`phòng ban `.length) : null,
    tinhNang: tinhNang ?? null,
    ky: kyTuChu(ky),
  };
}

const TO_CHUC_THANG: PhanQuyTac = { phamVi: 'to-chuc', tenPhongBan: null, tinhNang: null, ky: 'thang' };

const RONG: Omit<ThongTinHanMuc, 'loai' | 'maLoi' | 'cauGoc'> = {
  phamVi: null, tenPhongBan: null, tinhNang: null, ky: null, daDung: null, hanMuc: null, can: null, con: null,
  moLaiGio: null, moLaiNgay: null, dauKhongDu: null, lienHe: null, biCat: false, khacVanDung: false, viec: null,
  banChepConDung: false, boQuaTieuDe: false, heThongThuLai: false,
};

type PhanTach = Partial<ThongTinHanMuc> & { loai: LoaiHanMuc };

function loaiVuot(p: PhanQuyTac): LoaiHanMuc {
  return p.phamVi === 'nguoi' ? 'vuot-nguoi' : 'vuot-phong-ban';
}

function tach(cau: string): PhanTach | null {
  let m = HET_GOI.exec(cau);
  if (m) {
    return { ...TO_CHUC_THANG, loai: 'het-goi', daDung: docSo(m[1]), hanMuc: docSo(m[2]), moLaiGio: m[3] ?? null, moLaiNgay: m[4] ?? null,
      khacVanDung: Boolean(m[3]), lienHe: m[5] ? 'nen-tang' : null };
  }
  m = HET_GOI_BI_CAT.exec(cau);
  if (m) return { ...TO_CHUC_THANG, loai: 'het-goi', biCat: true, moLaiGio: m[1] ?? null, moLaiNgay: m[2] ?? null, lienHe: 'nen-tang' };

  m = VUOT.exec(cau);
  if (m) {
    const p = quyTac(m[1]!, m[2], m[3]!);
    return { ...p, loai: loaiVuot(p), daDung: docSo(m[4]), hanMuc: docSo(m[5]), moLaiGio: m[6]!, moLaiNgay: m[7]!, lienHe: 'to-chuc' };
  }
  m = VUOT_BI_CAT.exec(cau);
  if (m) {
    const p = quyTac(m[1]!, m[2], m[3]!);
    return { ...p, loai: loaiVuot(p), biCat: true, moLaiGio: m[4]!, moLaiNgay: m[5]!, lienHe: 'to-chuc' };
  }

  // Ba đường không đủ, mỗi đường hai câu: có quy tắc của Tenant Admin (nhóm giữa) hoặc của cả tổ chức.
  for (const [re, dau] of [[KHONG_DU_VAO_QT, 'dau-vao'], [KHONG_DU_AM_QT, 'am-thanh'], [KHONG_DU_RA_QT, 'dau-ra']] as const) {
    m = re.exec(cau);
    if (m) return { ...quyTac(m[2]!, m[3], m[4]!), loai: 'khong-du', dauKhongDu: dau, can: docSo(m[1]), con: docSo(m[5]), lienHe: 'to-chuc' };
  }
  for (const [re, dau] of [[KHONG_DU_VAO, 'dau-vao'], [KHONG_DU_AM, 'am-thanh'], [KHONG_DU_RA, 'dau-ra']] as const) {
    m = re.exec(cau);
    if (m) return { ...TO_CHUC_THANG, loai: 'khong-du', dauKhongDu: dau, can: docSo(m[1]), con: docSo(m[2]), lienHe: 'nen-tang' };
  }

  if (HET_CHEP_LOI.test(cau)) return { loai: 'het-goi', phamVi: 'to-chuc', viec: 'chep-loi' };
  if (HET_TOM_TAT.test(cau)) return { loai: 'het-goi', phamVi: 'to-chuc', viec: 'tom-tat', banChepConDung: true };
  if (HET_KY.test(cau)) return { loai: 'het-ky', lienHe: 'quan-tri' };
  if (CHUA_KIEM_CONG.test(cau)) return { loai: 'chua-kiem', khacVanDung: true };
  if (CHUA_KIEM_BIEN_BAN.test(cau)) return { loai: 'chua-kiem', heThongThuLai: true };
  return null;
}

/** Tách câu hết hạn mức; không phải câu của nền tảng hoặc khác khuôn thì `null`, nơi gọi hiện câu gốc. */
export function tachLoiHanMuc(cau: string | null | undefined, maLoi?: string | null): ThongTinHanMuc | null {
  if (!cau) return null;
  const cauGoc = cau.normalize('NFC').trim();
  const boc = BOC_TIEU_DE.exec(cauGoc);
  let than = boc ? boc[1]! : cauGoc;
  if (than.endsWith(DUOI_THU_LAI)) than = than.slice(0, -DUOI_THU_LAI.length);
  const phan = tach(than);
  if (!phan) return null;
  return { ...RONG, ...phan, boQuaTieuDe: Boolean(boc), maLoi: maLoi ?? null, cauGoc: cau };
}
